/**
 * Utilities for building human-readable card page URLs.
 *
 * URL format: /cards/{name-slug}/{setCode}/{collectorNumber}
 * Example:    /cards/mana-crypt/2xm/270
 */

/** Convert a card name to a URL-safe slug. */
export function toCardSlug(name: string): string {
  const slug = name
    .toLowerCase()
    // Transliterate non-ASCII chars found in EN card names (audit 2026-04-03)
    .replace(/[àáâãä]/g, 'a')
    .replace(/[éè]/g, 'e')
    .replace(/[íîï]/g, 'i')
    .replace(/[óöō]/g, 'o')
    .replace(/[úûü]/g, 'u')
    .replace(/ñ/g, 'n')
    .replace(/š/g, 's')
    .replace(/æ/g, 'ae')
    .replace(/[®꞉]/g, '')
    .replace(/\s*\/\/\s*/g, '-') // " // " → hyphen (split cards)
    .replace(/\//g, '-')         // "/" → hyphen (Who/What/When/Where/Why)
    .replace(/\s+/g, '-')        // spaces → hyphens
    .replace(/[^a-z0-9-]/g, '')  // strip remaining non-alphanumeric
    .replace(/-{2,}/g, '-')      // collapse consecutive hyphens
    .replace(/^-|-$/g, '');       // trim leading/trailing hyphens

  return slug || 'card'; // fallback for Un-set cards like ______ and _____
}

interface CardUrlData {
  name: string;
  setCode?: string | null;
  collectorNumber?: string | null;
  scryfallID?: string;
  scryfallId?: string; // some interfaces use lowercase 'd'
}

/** Build the canonical card URL path from card data.
 *  Falls back to UUID URL when setCode or collectorNumber is missing. */
export function buildCardUrl(card: CardUrlData): string {
  const setCode = card.setCode;
  const collectorNumber = card.collectorNumber;

  if (setCode && collectorNumber) {
    return `/cards/${toCardSlug(card.name)}/${setCode.toLowerCase()}/${collectorNumber}`;
  }

  // Fallback to UUID URL
  const uuid = card.scryfallID || card.scryfallId;
  if (uuid) {
    return `/cards/${uuid}`;
  }

  // Last resort: search
  return `/search/results?name=${encodeURIComponent(card.name)}&exact=true`;
}

/** Card-detail URL for the one specific printing a collection row or trade card represents.
 *
 *  Non-English printings deliberately resolve to their UUID URL: the slug route's
 *  by-printing lookup is language-agnostic and would land the user on the English
 *  printing instead. Same reasoning as getLegacyRedirectPath below.
 *
 *  Returns null when no printing can be identified (e.g. an oracle-level "Any Set"
 *  want row), so callers can omit the control rather than emit a link that points
 *  somewhere other than the row it sits on. */
export function buildPrintingUrl(
  card: (CardUrlData & { lang?: string | null }) | null | undefined
): string | null {
  if (!card?.name) return null;

  const uuid = card.scryfallID || card.scryfallId;
  const hasSlugParts = !!(card.setCode && card.collectorNumber);
  if (!uuid && !hasSlugParts) return null;

  if (card.lang && card.lang.toLowerCase() !== 'en') {
    return uuid ? `/cards/${uuid}` : null;
  }

  return buildCardUrl(card);
}

/** Legacy /cards/<uuid> → slug redirect target, or null when the page should
 *  stay on the UUID URL. Non-English printings stay on their UUID URL because
 *  the slug route's by-printing lookup is language-agnostic and resolves to
 *  the English printing. */
export function getLegacyRedirectPath(
  card: CardUrlData & { lang?: string | null }
): string | null {
  if (!card.setCode || !card.collectorNumber) return null;
  if (card.lang && card.lang.toLowerCase() !== 'en') return null;
  return buildCardUrl(card);
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Detect whether a URL parameter looks like a Scryfall UUID. */
export function isUuidFormat(param: string): boolean {
  return UUID_RE.test(param);
}
